import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, FolderKanban, Globe, Key, Copy, Check, AlignLeft, ShieldCheck, Code2 } from "lucide-react";
import { createProject } from "../../services/projectService";

const Field = ({ label, required, hint, children }) => (
  <div>
    <label className="form-label" style={{ fontWeight: 600, fontSize: 13, color: "#334155", marginBottom: 6 }}>
      {label}
      {required && <span className="text-danger ms-1">*</span>}
    </label>
    {children}
    {hint && <p style={{ margin: "5px 0 0", fontSize: 11, color: "#94a3b8" }}>{hint}</p>}
  </div>
);

const AddProject = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", description: "", url: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [revealedRawKey, setRevealedRawKey] = useState(null);
  const [revealedWidgetSecret, setRevealedWidgetSecret] = useState(null);
  const [revealedProjectPid, setRevealedProjectPid] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedWidget, setCopiedWidget] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState(false);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await createProject({
        projectName: form.name.trim(),
        description: form.description.trim(),
        websiteUrl: form.url.trim(),
      });
      const data = res?.data?.response ?? {};
      const rawKey = data?.apiKey?.rawKey;
      const rawSecret = data?.publicCredential?.rawSecret;
      if (rawKey || rawSecret) {
        setRevealedProjectPid(data?.projectPid ?? null);
        setRevealedRawKey(rawKey ?? null);
        setRevealedWidgetSecret(rawSecret ?? null);
      } else {
        navigate("/project-list");
      }
    } catch (err) {
      setError(err?.response?.data?.message ?? "Failed to create project. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(revealedRawKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleCopyWidget = () => {
    navigator.clipboard.writeText(revealedWidgetSecret);
    setCopiedWidget(true);
    setTimeout(() => setCopiedWidget(false), 1500);
  };

  const widgetSnippet = `<script src="https://yourdomain.com/widget/sdk.js" data-wk="${revealedProjectPid}" data-ws="${revealedWidgetSecret}" async></script>`;

  const handleCopySnippet = () => {
    navigator.clipboard.writeText(widgetSnippet);
    setCopiedSnippet(true);
    setTimeout(() => setCopiedSnippet(false), 1500);
  };

  const closeRevealModal = () => {
    setRevealedRawKey(null);
    setRevealedWidgetSecret(null);
    setRevealedProjectPid(null);
    navigate("/project-list");
  };

  return (
    <main className="app-wrapper url-list-page">
      <div className="container-fluid" style={{ maxWidth: 1100 }}>

        {/* Page header */}
        <div style={{ marginBottom: 22 }}>
          <p style={{ margin: 0, fontSize: 13, color: "var(--muteColor)" }}>
            Fill in the details below to create a new project.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* ── Project Info ── */}
            <div>
              <div
                style={{
                  background: "#fff",
                  border: "1px solid var(--borderColor)",
                  borderRadius: 14,
                  boxShadow: "0 1px 3px rgba(15,23,42,.05), 0 4px 12px rgba(15,23,42,.03)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "16px 20px",
                    borderBottom: "1px solid var(--borderColor)",
                    background: "#fafbfc",
                  }}
                >
                  <div
                    style={{
                      width: 38, height: 38, borderRadius: 10,
                      background: "var(--primaryTint)", color: "var(--primaryColor)",
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}
                  >
                    <Building2 size={18} />
                  </div>
                  <div>
                    <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Project Information</h6>
                    <p style={{ margin: 0, fontSize: 12, color: "var(--muteColor)" }}>Basic details about the project</p>
                  </div>
                </div>

                <div style={{ padding: "22px 20px", display: "flex", flexDirection: "column", gap: 18 }}>

                  <Field label="Project Name" required>
                    <div style={{ position: "relative" }}>
                      <FolderKanban size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                      <input
                        type="text"
                        className="form-control input-tags"
                        placeholder="e.g. BrightWave Project"
                        value={form.name}
                        onChange={set("name")}
                        required
                        minLength={2}
                        maxLength={100}
                        style={{ paddingLeft: 36 }}
                      />
                    </div>
                  </Field>

                  <Field label="Description">
                    <div style={{ position: "relative" }}>
                      <AlignLeft size={14} style={{ position: "absolute", left: 12, top: 12, color: "#94a3b8" }} />
                      <textarea
                        className="form-control input-tags"
                        placeholder="Briefly describe this project..."
                        value={form.description}
                        onChange={set("description")}
                        maxLength={500}
                        rows={3}
                        style={{ paddingLeft: 36, resize: "vertical" }}
                      />
                    </div>
                  </Field>

                  <Field label="Website URL" hint="Subdomain or domain where this project's AI widget is deployed.">
                    <div
                      style={{
                        display: "flex", alignItems: "stretch",
                        border: "1px solid #415a774d", borderRadius: 8,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          display: "flex", alignItems: "center", gap: 6,
                          padding: "0 12px",
                          background: "#f8fafc",
                          borderRight: "1px solid #e8edf3",
                          color: "#64748b", fontSize: 12, fontWeight: 600,
                          whiteSpace: "nowrap", userSelect: "none", flexShrink: 0,
                        }}
                      >
                        <Globe size={13} />
                        https://
                      </div>
                      <input
                        type="text"
                        style={{
                          flex: 1, border: "none", outline: "none",
                          padding: "9px 12px", fontSize: 13,
                          background: "transparent", minWidth: 0,
                        }}
                        placeholder="yourproject.example.com"
                        value={form.url}
                        onChange={set("url")}
                        maxLength={255}
                      />
                    </div>
                  </Field>

                </div>
              </div>
            </div>

          </div>

          {/* ── Footer actions ── */}
          <div
            style={{
              display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10,
              marginTop: 24,
              padding: "16px 20px",
              borderRadius: 12,
              boxShadow: "0 1px 3px rgba(15,23,42,.05)",
            }}
          >
            {error && <span style={{ fontSize: 12, color: "#dc2626", marginRight: "auto" }}>{error}</span>}
            <button
              type="button"
              className="btn btn-light"
              style={{ borderRadius: 8, minHeight: 40, paddingInline: 20, fontSize: 13, fontWeight: 600 }}
              onClick={() => navigate("/project-list")}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary d-flex align-items-center gap-2"
              style={{ borderRadius: 8, minHeight: 40, paddingInline: 20, fontSize: 13 }}
              disabled={loading || !form.name.trim()}
            >
              {loading ? "Creating..." : "Add Project"}
            </button>
          </div>
        </form>

      </div>

      {/* Raw Key / Widget Secret Reveal Modal — shown once after creation */}
      {(revealedRawKey || revealedWidgetSecret) && (
        <div style={{ position: "fixed", inset: 0, zIndex: 2100, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 14, width: 520, maxWidth: "92vw", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(15,23,42,0.2)" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--borderColor)", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "#fff7ed", color: "var(--orangeColor)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Key size={15} />
              </div>
              <div>
                <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Save your credentials</h6>
                <p style={{ margin: 0, fontSize: 11, color: "#dc2626", fontWeight: 600 }}>These are shown only once — copy them now.</p>
              </div>
            </div>
            <div style={{ padding: "20px" }}>

              {revealedRawKey && (
                <>
                  <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 700, color: "#334155" }}>API Key</p>
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px", marginBottom: 18, display: "flex", alignItems: "center", gap: 10 }}>
                    <code style={{ flex: 1, fontSize: 12, color: "#334155", fontFamily: "monospace", wordBreak: "break-all", userSelect: "all" }}>
                      {revealedRawKey}
                    </code>
                    <button
                      className={`btn btn-sm d-flex align-items-center gap-1 ${copied ? "btn-success" : "btn-outline-primary"}`}
                      style={{ fontSize: 12, borderRadius: 7, flexShrink: 0 }}
                      onClick={handleCopyKey}
                    >
                      {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                    </button>
                  </div>
                </>
              )}

              {revealedWidgetSecret && (
                <>
                  <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 700, color: "#334155", display: "flex", alignItems: "center", gap: 6 }}>
                    <ShieldCheck size={13} /> Widget Public Secret
                  </p>
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px", marginBottom: 10, display: "flex", alignItems: "center", gap: 10 }}>
                    <code style={{ flex: 1, fontSize: 12, color: "#334155", fontFamily: "monospace", wordBreak: "break-all", userSelect: "all" }}>
                      {revealedWidgetSecret}
                    </code>
                    <button
                      className={`btn btn-sm d-flex align-items-center gap-1 ${copiedWidget ? "btn-success" : "btn-outline-primary"}`}
                      style={{ fontSize: 12, borderRadius: 7, flexShrink: 0 }}
                      onClick={handleCopyWidget}
                    >
                      {copiedWidget ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                    </button>
                  </div>
                  <p style={{ margin: "0 0 14px", fontSize: 11, color: "#94a3b8" }}>
                    Regenerating this later will invalidate it immediately, breaking any deployed widget until updated.
                  </p>

                  <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 700, color: "#334155", display: "flex", alignItems: "center", gap: 6 }}>
                    <Code2 size={13} /> Embed Snippet
                  </p>
                  <div style={{ background: "#0f172a", borderRadius: 10, padding: "12px 14px", marginBottom: 4, display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <code style={{ flex: 1, fontSize: 11.5, color: "#e2e8f0", fontFamily: "monospace", wordBreak: "break-all", userSelect: "all" }}>
                      {widgetSnippet}
                    </code>
                    <button
                      className={`btn btn-sm d-flex align-items-center gap-1 ${copiedSnippet ? "btn-success" : "btn-outline-light"}`}
                      style={{ fontSize: 12, borderRadius: 7, flexShrink: 0 }}
                      onClick={handleCopySnippet}
                    >
                      {copiedSnippet ? <Check size={12} /> : <Copy size={12} />}
                    </button>
                  </div>
                </>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                <button
                  className="btn btn-primary"
                  style={{ fontSize: 13, borderRadius: 8 }}
                  onClick={closeRevealModal}
                >
                  I've saved it, close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default AddProject;
